<template>
  <div v-if="company" class="general-infos">
    <div class="title">{{ company.denomination }}</div>
    <div class="container">
      <div class="col">
        <div class="text address">
          {{ company.address && company.address.lines ? company.address.lines.reduce((a, v) => `${a} ${v}`, '') : '' }}
        </div>
        <div class="text postal-code">
          {{ company && company.address && company.address.zipCode ? company.address.zipCode : '' }},
          {{ company && company.address && company.address.city ? company.address.city : '' }}
        </div>
        <div class="text siren">
          {{ $t('SIREN') }} :
          {{ $utils.sirenFormat(company.siren) }}
        </div>
        <div v-if="company.rcs" class="text greffe">{{ $t('registryCommercialCourtOf') }} {{ company.rcs }}</div>
      </div>
      <div class="col margin">
        <div class="text date">
          <span>{{ $t('closureDate') }}</span>
          {{ getInfos(company, 0) }}
        </div>
        <div class="text ca">
          <span>{{ $t('ca') }}</span>
          {{ getInfos(company, 1) }}
        </div>
        <div class="text result">
          <span>{{ $t('result') }}</span>
          {{ getInfos(company, 2) }}
        </div>
      </div>
      <div v-if="!isMonitored && !isMonitoring && !company.radiationDate" class="col">
        <div v-if="!company.isUnregistered" class="main-button min-width" @click="setUp">
          {{ $t('setUpNewMonitor') }}
        </div>
      </div>
      <div v-else-if="isMonitored" class="col flex-end">
        <div class="main-button blue" @click="exportPortfolio">
          <div v-if="loadingPortfolio" class="lds-ellipsis">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
          <IconImport v-else class="icon" />
          {{ !loadingPortfolio ? $t('exportAllCompanyData') : '' }}
        </div>
      </div>
    </div>

    <div v-if="isMonitoring || isMonitored" class="container space-between color" :class="{ edit: isMonitoring }">
      <div class="col">
        <div class="line title green">{{ $t('monitoringInfos') }}</div>
        <div v-if="portfolio && portfolio.company && !portfolio.company.pending && portfolio.createdAt" class="line small">
          {{ $t('underMontoringOn') }}
          {{ $utils.formatDate(portfolio.createdAt) }}
        </div>
      </div>
      <div class="col">
        <div class="line title">
          {{ $t('importCompanies.monitoringInfos.0') }}
        </div>
        <div v-if="!isMonitoring" class="line monitoring">
          <MonitoringType :level="getMonitoringInfos.level" :label="getMonitoringInfos.label" />
        </div>
        <div v-else class="line monitoring">
          <Dropdown
            class="priority-dropdown"
            :config="{
              inputSize: 34,
              active: isMonitoring,
              showActivatedOnly: true,
            }"
            :values="getPackages()"
            :placeholder="$t('chooseMonitoringLevel')"
            :on-change="
              value => {
                monitoringLevel = value.publicId;
                isChanged = true;
              }
            "
            :value="monitoringLevel"
          >
            <template #placeholder="{ value, isPlaceholder }">
              <MonitoringType v-if="!isPlaceholder" :level="getMonitoringInfos.level" :label="getMonitoringInfos.label" />
              <div v-else class="value grey">{{ value }}</div>
            </template>
            <template #choices="{ choice }">
              <MonitoringType :class="{ highlight: choice === monitoringLevel }" :level="choice.level" :label="choice.label" />
            </template>
          </Dropdown>
        </div>
      </div>

      <div class="col">
        <div class="line title">{{ $t('internalId') }}</div>
        <EditorField
          class="editor"
          :placeholder="$t('addInternalId')"
          :config="{ inputSize: 34, active: isMonitoring }"
          :on-change="
            value => {
              internalId = value;
              isChanged = true;
            }
          "
          :value="internalId"
        />
      </div>

      <div class="col">
        <div class="line title">{{ $t('manager') }}</div>
        <div v-if="!isMonitoring" class="line">{{ getManagerInfos.firstName }} {{ getManagerInfos.lastName }}</div>
        <div v-else class="line monitoring">
          <Dropdown
            class="manager-dropdown"
            :config="{
              inputSize: 34,
              color: 'white',
              active: isMonitoring,
              showActivatedOnly: true,
            }"
            :values="users"
            :placeholder="$t('manager')"
            :on-change="
              value => {
                manager = value.id;
                isChanged = true;
              }
            "
            :value="manager"
          >
            <template #placeholder="{ value, isPlaceholder }">
              <div v-if="!isPlaceholder" class="manager">{{ getManagerInfos.firstName }} {{ getManagerInfos.lastName }}</div>
              <div v-else class="manager grey">{{ value }}</div>
            </template>
            <template #choices="{ choice }">
              <div class="manager white" :class="{ highlight: choice === manager }">{{ choice.firstName }} {{ choice.lastName }}</div>
            </template>
          </Dropdown>
        </div>
      </div>

      <div v-if="!(portfolio && portfolio.company.pending) && !company.radiationDate" class="col monitor">
        <div v-if="isMonitored && !isMonitoring" class="line main-button" @click.stop="edit">
          {{ $t('changeInfo') }}
        </div>
        <div v-if="isMonitored && isMonitoring" class="line main-button" :class="{ disabled: !isChanged }" @click.stop="patchMonitoring">
          {{ $t('changeInfo') }}
        </div>
        <div v-if="!isMonitored" class="line main-button" :class="{ disabled: isCompleteInputs }" @click.stop="setUpMonitoring">
          <div v-if="loading" class="lds-ellipsis">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
          {{ loading ? '' : $t('setUpNewMonitor') }}
        </div>
        <div v-if="!isMonitored || isMonitoring" class="line main-button red" @click.stop="cancel">
          {{ $t('cancel') }}
        </div>
      </div>
      <div v-if="portfolio && portfolio.company && portfolio.company.pending" class="col monitor pending">Mise sous surveillance en cours</div>
    </div>
    <div v-if="company.radiationDate" class="container space-between">
      <div class="info warning">
        <div class="icon"></div>
        <div class="text">
          {{
            $t(!isMonitored ? 'radiatedCompany' : 'newlyRadiatedCompany', {
              date: $utils.formatDate(company.radiationDate),
            })
          }}
        </div>
      </div>
    </div>
    <div v-else-if="!isMonitored && company.isRcs" class="container space-between">
      <div class="info" :class="{ partialInfo: !company.fullCompletion }">
        <div class="icon"></div>
        <div class="text">
          {{ !company.fullCompletion ? $t('partialMonitoring') : $t('completeMonitoring') }}
        </div>
      </div>
    </div>
    <div v-else-if="!isMonitored && !company.isRcs" class="container space-between">
      <div class="info partialInfo">
        <div class="icon"></div>
        <div class="text">{{ $t('unregisteredCompany') }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Dropdown from '@/components/Dropdown';
import EditorField from '@/components/EditorField';
import MonitoringType from '@/components/monitoringType';

export default {
  components: { Dropdown, EditorField, MonitoringType },

  props: {
    company: {
      type: Object,
      required: true,
    },

    setMonitored: {
      type: Function,
      required: false,
      default: () => null,
    },
  },

  data() {
    return {
      loading: false,
      monitoringLevel: '',
      isEdit: false,
      isMonitoring: false,
      manager: '',
      internalId: '',
      packages: [],
      users: [],
      portfolio: null,
      isMonitored: false,
      isChanged: false,
      loadingPortfolio: false,
      oldValue: null,
      client: null,
    };
  },

  computed: {
    ...mapGetters(['user']),

    isCompleteInputs() {
      return !(this.monitoringLevel && this.manager);
    },

    getMonitoringInfos() {
      const info = this.packages.find(value => value.publicId === this.monitoringLevel);
      return info;
    },

    getManagerInfos() {
      const info = this.users.find(value => value.id === this.manager);
      return info;
    },

    confidentialAccess() {
      return this.user.client.confidentialAccess;
    },
  },

  async created() {
    this.packages = (await this.$store.dispatch('getPackagesLevel')).items;
    this.client = await this.$store.dispatch('getUsers', {
      id: this.user.client.id,
    });

    this.users = this.client.users;
    this.getPortfolioInfos();
    this.monitoringLevel = this.client.defaultPackageLevelPublicId;
  },

  methods: {
    async exportPortfolio() {
      this.loadingPortfolio = true;
      const result = await this.$store.dispatch('getPortfolioExport', {
        siren: this.company.siren,
      });
      const content = result.headers['content-disposition'];
      const filename = content
        .split(';')
        .find(n => n.includes('filename='))
        .replace('filename=', '');
      const url = window.URL.createObjectURL(new Blob([result.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      const child = document.body.appendChild(link);
      link.click();
      child.remove();
      window.URL.revokeObjectURL(url);
      this.loadingPortfolio = false;
    },

    isSevenYearRadiated(company) {
      if (!company.unregistrationDate) return false;
      const date = new Date(company.unregistrationDate);
      return date.getFullYear() < new Date().getFullYear() - 7;
    },

    getInfos(company, type) {
      const types = {
        0: 'NONE',
        1: 'NON_TENU_DE_DEPOSER',
        2: 'GIE',
        3: 'PERSONNE_PHYSIQUE',
        4: 'COMMERCANTS',
        5: 'EIRL',
        6: 'FJ_ETRANGERE',
        7: 'STE_RECEMMENT_IMMAT',
        8: 'ANCIEN_DOSSIER',
      };
      const key = types[company.notRequiredToSubmit];
      if (key === 'NONE') {
        if (
          !this.confidentialAccess &&
          company.keyNumbers &&
          company.keyNumbers[0] &&
          (company.keyNumbers[0].confidential || company.keyNumbers[0].numbers.confidentialResult) &&
          (type === 1 || type === 2)
        ) {
          return type === 1 ? this.$t('accountConfidentiality') : this.$t('accountResultConfidentiality');
        }
        if (type === 0) {
          return company && company.keyNumbers && company.keyNumbers[0] && company.keyNumbers[0].closingDate
            ? this.$utils.formatDate(company.keyNumbers[0].closingDate)
            : '-';
        }
        if (type === 1) {
          return company && company.keyNumbers && company.keyNumbers[0] && company.keyNumbers[0].numbers.salesRevenue
            ? this.$utils.splitStringBySegmentLength(company.keyNumbers[0].numbers.salesRevenue) + ' €'
            : this.$t('notAvailable');
        }
        if (type === 2) {
          return company && company.keyNumbers && company.keyNumbers[0] && company.keyNumbers[0].numbers.result
            ? this.$utils.splitStringBySegmentLength(company.keyNumbers[0].numbers.result) + ' €'
            : this.$t('notAvailable');
        }
      } else {
        return this.$t(`notRequiredToDeposit.${key}`);
      }
    },

    setUp() {
      this.isMonitoring = !this.isMonitoring;
      this.manager = this.user.id;
      this.isChanged = false;
    },

    edit() {
      this.oldValue = {
        monitoringLevel: `${this.monitoringLevel}`,
        manager: `${this.manager}`,
        internalId: `${this.internalId}`,
      };

      this.isMonitoring = !this.isMonitoring;
      this.isChanged = false;
    },

    cancel() {
      this.isMonitoring = false;

      this.monitoringLevel = this.oldValue.monitoringLevel;
      this.manager = this.oldValue.manager;
      this.internalId = this.oldValue.internalId;
    },

    async setUpMonitoring() {
      if (!this.isChanged && !this.monitoringLevel) return;
      const result = await this.$store.dispatch('setPopup', {
        popupType: 'SetUpMonitoring',
      });
      if (result) {
        if (this.monitoringLevel || this.internalId || this.manager) {
          this.loading = true;
          await this.$store.dispatch('startMonitoring', {
            siren: this.company.siren,
            internalRef: this.internalId,
            publicManagerId: this.manager,
            packageLevelPublicId: this.monitoringLevel,
          });
          this.isMonitoring = false;
          await this.getPortfolioInfos();
          this.loading = false;
        }
      }
    },

    async patchMonitoring() {
      if (!this.isChanged) return;
      const archivePackage = this.packages.find(p => !p.level);
      if (this.portfolio.packageLevelPublicId === archivePackage.publicId && this.monitoringLevel !== archivePackage.publicId) {
        const result = await this.$store.dispatch('setPopup', {
          popupType: 'ChangeMonitoring',
        });
        if (result) {
          this.updatePackage();
        }
      } else {
        this.updatePackage();
      }
    },

    async updatePackage() {
      if (this.monitoringLevel || this.internalId || this.manager) {
        this.loading = true;
        this.isMonitoring = false;
        await this.$store.dispatch('patchMonitoring', {
          siren: this.company.siren,
          internalRef: this.portfolio.internalRef === this.internalId ? undefined : this.internalId,
          publicManagerId: this.manager === this.portfolio.managerPublicId ? undefined : this.manager,
          packageLevelPublicId: this.monitoringLevel === this.portfolio.packageLevelPublicId ? undefined : this.monitoringLevel,
        });
        window.location.reload();
        this.loading = false;
      }
    },

    async getPortfolioInfos() {
      const result = await this.$store.dispatch('getPortfolioBySiren', {
        siren: this.company.siren,
      });

      if (result.exists) {
        this.portfolio = result;
        this.isMonitored = true;
        this.setMonitored(true);
        this.internalId = this.portfolio.internalRef;
        this.manager = this.portfolio.managerPublicId;
        this.monitoringLevel = this.portfolio.packageLevelPublicId;
      } else {
        this.isMonitored = false;
        this.setMonitored(false);
      }
    },

    getPackages() {
      if (this.company.isUnregistered) {
        return this.packages.filter(e => e.level === 0);
      } else {
        if (!this.portfolio) {
          return this.packages.filter(e => e.level !== 0);
        } else {
          return this.packages;
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive.scss';
@use '@/styles/fonts.scss';
.general-infos {
  width: 100%;

  .title {
    width: 100%;
    font-weight: 500;
    font-size: 30px;
    color: colors.$darkblue-color;
    margin: 0 0 20px;
  }

  .container {
    display: flex;
    align-items: center;
    align-items: center;
    flex-grow: 1;

    &.space-between {
      justify-content: space-between;
    }

    &.color {
      background: colors.$unread-table-color;
      margin-top: 24px;
      padding: 36px;
      border-radius: 5px;
    }

    .info {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      margin: 12px 0 0 0;

      &.partialInfo {
        .icon {
          border: 2px solid colors.$orange-color !important;
          &:before,
          &:after {
            background: colors.$orange-color !important;
          }
        }
        .text {
          color: colors.$orange-color;
        }
      }

      &.warning {
        .icon {
          border: 2px solid colors.$red-color !important;
          &:before,
          &:after {
            background: colors.$red-color !important;
          }
        }
        .text {
          color: colors.$red-color;
        }
      }

      .icon {
        margin: 0 12px 0 0;
        position: relative;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        border: 2px solid colors.$lightblue-color;
        flex-shrink: 0;

        &:after {
          position: absolute;
          content: '';
          bottom: 3px;
          left: calc(50% - 1px);
          right: calc(50% - 1px);
          height: 8px;
          background: colors.$lightblue-color;
        }

        &:before {
          position: absolute;
          content: '';
          top: 3px;
          left: calc(50% - 1px);
          right: calc(50% - 1px);
          height: 2px;
          background: colors.$lightblue-color;
        }
      }

      .text {
        color: colors.$lightblue-color;
        font-size: 14px;
        opacity: 0.8;
      }
    }

    .col {
      &.flex-end {
        display: flex;
        align-items: flex-end;
        justify-content: center;
        flex-direction: column;
        flex-grow: 1;

        .main-button {
          &.blue {
            max-width: 326px;
            background: colors.$lightblue-color;
            border-radius: 5px;
            font-size: 14px;
            font-weight: 500;
            padding: 8px 12px;
            .icon {
              margin-right: 8px;
            }
          }
        }
      }

      &.monitor {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;

        &.pending {
          color: colors.$lightgrey-color;
        }

        .main-button {
          margin-left: 48px;
          display: flex;
          align-items: center;
          justify-content: center;

          &.disabled {
            background: colors.$lightgrey-color;
            cursor: not-allowed;
          }

          &:nth-child(2n) {
            margin-top: 10px;
          }

          &.red {
            display: flex;
            justify-content: center;
            align-items: center;
            background: colors.$red-color;
            width: 100px;
            min-width: 100px;
          }
        }
      }

      .editor {
        width: 90px;
      }

      .main-button {
        &.min-width {
          min-width: 300px;
        }
      }

      &.margin {
        margin: 0 10%;
      }

      .manager-dropdown,
      .priority-dropdown {
        width: 150px;

        .manager,
        .value {
          max-width: 110px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;

          &.grey {
            color: colors.$lightgrey-color;
          }
        }
      }

      .text {
        font-size: 15px;
        color: colors.$darkgrey-color;

        &.postal-code {
          margin: 0 0 12px;
        }

        &.date,
        &.ca,
        &.result {
          margin: 0 0 16px;
          display: flex;
          justify-content: space-between;
          text-align: right;

          span {
            color: colors.$black-color;
            font-weight: 500;
            margin: 0 36px 0 0;
            width: 124px;
            text-align: left;
          }
        }
      }

      .line {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        color: colors.$black-color;
        font-size: 13px;
        height: 36px;

        .priority-dropdown {
          width: 100%;
          height: 35px;

          .priority-level {
            display: flex;
            align-items: center;

            &:not(:nth-child(2n)) {
              flex-shrink: 0;
              width: 17px;
              height: 17px;
              background: colors.$red-color;
              color: colors.$white-color;
              border-radius: 50%;
              font-size: 12px;
              margin: 0 8px 0 0;
              display: flex;
              align-items: center;
              justify-content: center;
            }

            .value {
              color: colors.$red-color;
            }
          }
        }

        &.title {
          font-weight: 500;
          font-size: 14px;
          color: colors.$grey-color;
          margin-bottom: 2px;
          padding-top: 2px;
          box-sizing: border-box;
          height: fit-content;

          &.green {
            color: colors.$green-color;
            font-size: 20px;
            padding: 0;
          }
        }

        &.small {
          font-size: 13px;
          color: colors.$grey-color;
        }

        &.monitoring {
          display: flex;
          flex-direction: row;

          .item {
            &:nth-child(2n) {
              color: colors.$red-color;
              font-size: 14px;
            }

            &:not(:nth-child(2n)) {
              flex-shrink: 0;
              width: 17px;
              height: 17px;
              background: colors.$red-color;
              color: colors.$white-color;
              border-radius: 50%;
              font-size: 12px;
              margin: 0 8px 0 0;
              display: flex;
              align-items: center;
              justify-content: center;
            }
          }
        }

        &.main-button {
          color: colors.$white-color;
        }
      }
    }

    &.edit {
      .col {
        height: 64px;
        .line {
          margin-bottom: 14px;

          &.small {
            margin: 0;
            margin-top: -12px;
          }
        }
      }
    }
  }
}

@media only screen and (max-width: responsive.$layout-breakpoint-small) {
  .container {
    flex-direction: column;
    align-items: flex-start !important;
  }

  .col {
    margin: 12px 0;
  }

  .col.margin {
    margin: 12px 0 !important;
  }
}
</style>
